import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Gauge, AlertTriangle, Clock, FileText, TrendingUp, Scale, BookMarked,
  ChevronRight, CheckCircle2, XCircle, Sparkles, ExternalLink,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

/**
 * Readiness Dashboard — the AI's opening screen inside the Grant Writer Workspace.
 * Displays: overall readiness %, missing institutional documents, upcoming deadlines,
 * grants in progress, pending drafts, letters of support, budget status,
 * fiscal sponsor status, highest-priority recommendation.
 *
 * Also renders the Doctrine Status band (active directives + templates + voice profile).
 */
export default function ReadinessDashboard({ staffId, onOpenGrant }) {
  const [data, setData] = useState(null);
  const [doctrine, setDoctrine] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([
        api.get(`/staff/${staffId}/readiness-dashboard`),
        api.get(`/staff/${staffId}/doctrine-status`),
      ]);
      setData(r.data);
      setDoctrine(d.data);
    } catch (e) { console.debug("readiness: load failed", e?.message); }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  if (!data || !doctrine) {
    return <div className="rounded-xl border border-line bg-admin-surface p-6 text-sm text-ink-soft">Loading readiness dashboard…</div>;
  }

  const pct = data.readiness_pct;
  const pctColor = pct >= 80 ? "text-forest" : pct >= 50 ? "text-terracotta" : "text-ink-soft";
  const barColor = pct >= 80 ? "bg-forest" : pct >= 50 ? "bg-terracotta" : "bg-line";

  return (
    <div className="space-y-4" data-testid="readiness-dashboard">
      {/* Doctrine Status Band */}
      <div className="rounded-xl border border-line bg-ink/95 text-bone p-4" data-testid="doctrine-status-band">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Scale className="h-4 w-4 text-terracotta" strokeWidth={1.5} />
            <span className="overline text-bone/80">Constitutional Foundation</span>
          </div>
          <Link to="/admin/doctrine" data-testid="open-doctrine-page" className="text-xs text-bone/70 hover:text-bone flex items-center gap-1">
            Manage <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <DoctrineTile
            testId="doctrine-tile-active"
            label="Active Doctrine"
            value={doctrine.doctrine.count > 0 ? `${doctrine.doctrine.count} directive${doctrine.doctrine.count === 1 ? "" : "s"}` : "None"}
            sub={doctrine.doctrine.active_directives.slice(0, 2).map(d => `#${d.directive_id} v${d.version}`).join(" · ") || "— constitutional gap —"}
            tone={doctrine.doctrine.count > 0 ? "forest" : "warn"}
            updated={doctrine.doctrine.last_updated}
          />
          <DoctrineTile
            testId="doctrine-tile-verbatim"
            label="Verbatim Templates"
            value={`${doctrine.templates.verbatim_count}`}
            sub="Quoted word-for-word"
            tone={doctrine.templates.verbatim_count > 0 ? "forest" : "warn"}
            updated={doctrine.templates.last_updated}
          />
          <DoctrineTile
            testId="doctrine-tile-adapted"
            label="Adapted Templates"
            value={`${doctrine.templates.adapted_count}`}
            sub="Scaffolding for narrative"
            tone={doctrine.templates.adapted_count > 0 ? "forest" : "warn"}
            updated={doctrine.templates.last_updated}
          />
          <DoctrineTile
            testId="doctrine-tile-voice"
            label="Founder Voice Profile"
            value={doctrine.voice_profile.status === "established" ? "Established" : "Learning"}
            sub={`${doctrine.voice_profile.edits_captured} edits · ${doctrine.voice_profile.rejections_captured} rej · ${doctrine.voice_profile.revisions_captured} rev`}
            tone={doctrine.voice_profile.status === "established" ? "forest" : "muted"}
            updated={doctrine.voice_profile.last_updated}
          />
        </div>
      </div>

      {/* Main Readiness Panel */}
      <div className="rounded-xl border border-line bg-admin-surface p-5" data-testid="readiness-main">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Gauge className="h-5 w-5 text-terracotta" strokeWidth={1.5} />
            <div>
              <div className="overline">Institution Readiness</div>
              <h2 className="font-serif text-2xl text-ink">Grant Office · Executive View</h2>
            </div>
          </div>
          <div className="text-right">
            <div className={`font-serif text-5xl leading-none ${pctColor}`} data-testid="readiness-pct">{pct}%</div>
            <div className="text-[10px] text-ink-soft uppercase tracking-widest mt-1">Overall Readiness</div>
          </div>
        </div>

        <div className="h-1.5 bg-bone rounded-full overflow-hidden mb-5">
          <div className={`h-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
        </div>

        <div className="grid grid-cols-4 gap-2 mb-5 text-[10px]">
          <ComponentBar label="Templates" value={data.readiness_components.templates} max={40} />
          <ComponentBar label="Doctrine" value={data.readiness_components.doctrine} max={20} />
          <ComponentBar label="Fiscal Sponsor" value={data.readiness_components.fiscal_sponsor} max={20} />
          <ComponentBar label="Confirmation Gap" value={data.readiness_components.confirmation_gap} max={20} />
        </div>

        {/* Metric strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricTile
            icon={FileText} label="Missing Documents"
            value={data.missing_documents.length} sub={`of ${data.institutional_documents.length}`}
            tone={data.missing_documents.length > 0 ? "warn" : "forest"} testId="metric-missing-docs"
          />
          <MetricTile
            icon={Clock} label="Upcoming Deadlines"
            value={data.upcoming_deadlines.length} sub={`${data.critical_deadlines_count} critical`}
            tone={data.critical_deadlines_count > 0 ? "warn" : data.upcoming_deadlines.length > 0 ? "terracotta" : "forest"}
            testId="metric-upcoming"
          />
          <MetricTile
            icon={TrendingUp} label="Grants in Progress"
            value={data.grants_in_progress} sub={`${data.open_grants_total} total open`}
            tone="forest" testId="metric-in-progress"
          />
          <MetricTile
            icon={AlertTriangle} label="Drafts Pending Approval"
            value={data.pending_drafts} sub={`${data.approved_drafts} approved to date`}
            tone={data.pending_drafts > 0 ? "terracotta" : "muted"} testId="metric-pending-drafts"
          />
        </div>

        {/* Secondary status */}
        <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
          <StatusChip
            label="Fiscal Sponsor" state={data.fiscal_sponsor_status}
            good="ready" testId="status-fiscal"
          />
          <StatusChip
            label="Budget Template" state={data.budget_status}
            good="ready" testId="status-budget"
          />
          <StatusChip
            label="Letters of Support"
            state={data.letters_of_support_needed === 0 ? "ready" : `${data.letters_of_support_needed} needed`}
            good="ready" testId="status-los"
          />
        </div>

        {/* Top recommendation */}
        {data.top_recommendation && (
          <div className="mt-5 rounded-lg border border-terracotta/40 bg-terracotta/5 p-4" data-testid="top-recommendation">
            <div className="flex items-start gap-3">
              <Sparkles className="h-4 w-4 text-terracotta mt-1" strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <div className="overline text-terracotta">Today&rsquo;s Highest-Priority Recommendation</div>
                <div className="font-serif text-xl text-ink mt-1">{data.top_recommendation.name}</div>
                <div className="text-xs text-ink-soft mt-1 flex items-center gap-2 flex-wrap">
                  <span>Priority <strong className="text-ink">{data.top_recommendation.priority_score}</strong></span>
                  <span>·</span>
                  <span>Fit <strong className="text-ink">{data.top_recommendation.fit_score}</strong></span>
                  {data.top_recommendation.deadline_urgency && (
                    <>
                      <span>·</span>
                      <Badge className={`text-[9px] uppercase ${data.top_recommendation.deadline_urgency === "critical" ? "bg-terracotta text-bone" : "bg-bone text-ink border border-line"}`}>{data.top_recommendation.deadline_urgency}</Badge>
                    </>
                  )}
                  {data.top_recommendation.founder_confirmation_count > 0 && (
                    <>
                      <span>·</span>
                      <span className="text-terracotta">{data.top_recommendation.founder_confirmation_count} confirmations pending</span>
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={() => onOpenGrant?.(data.top_recommendation.grant_id)}
                data-testid="open-top-rec"
                className="shrink-0 h-8 px-3 rounded-full text-xs font-medium bg-forest text-bone hover:bg-ink flex items-center gap-1.5"
              >
                Open <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        )}

        {/* Ranked opportunities + deadlines */}
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          {data.upcoming_deadlines.length > 0 && (
            <div>
              <div className="overline mb-2 flex items-center gap-2"><Clock className="h-3 w-3 text-terracotta" strokeWidth={1.5} />Upcoming Deadlines</div>
              <ul className="space-y-1.5 text-sm" data-testid="upcoming-list">
                {data.upcoming_deadlines.slice(0, 6).map(u => (
                  <li key={u.grant_id}>
                    <button onClick={() => onOpenGrant?.(u.grant_id)} className="w-full text-left px-3 py-2 rounded border border-line bg-bone/40 hover:bg-bone flex items-center gap-3">
                      <span className={`w-1.5 h-1.5 rounded-full ${u.urgency === "critical" ? "bg-terracotta" : u.urgency === "high" ? "bg-terracotta/50" : "bg-line"}`} />
                      <span className="flex-1 truncate text-ink">{u.name}</span>
                      <span className="text-xs text-ink-soft">{u.days_remaining < 0 ? "past" : `${u.days_remaining}d`}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.missing_documents.length > 0 && (
            <div>
              <div className="overline mb-2 flex items-center gap-2"><FileText className="h-3 w-3 text-terracotta" strokeWidth={1.5} />Missing Institutional Templates</div>
              <ul className="space-y-1.5 text-sm" data-testid="missing-docs-list">
                {data.missing_documents.map(d => (
                  <li key={d.slug} className="flex items-center gap-2 px-3 py-2 rounded border border-line bg-bone/40">
                    <XCircle className="h-3.5 w-3.5 text-terracotta shrink-0" strokeWidth={1.5} />
                    <span className="flex-1 text-ink">{d.label}</span>
                    <code className="text-[10px] text-ink-soft">{d.slug}</code>
                  </li>
                ))}
              </ul>
              <Link to="/admin/doctrine" className="text-xs text-terracotta hover:underline mt-2 inline-block" data-testid="add-templates-link">
                → Add these templates in the Doctrine page
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DoctrineTile({ label, value, sub, tone, updated, testId }) {
  const toneCls = {
    forest: "text-forest",
    warn: "text-terracotta",
    muted: "text-bone/70",
  }[tone] || "text-bone";
  return (
    <div className="border-l-2 border-bone/20 pl-3" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-widest text-bone/60">{label}</div>
      <div className={`font-serif text-lg mt-0.5 ${toneCls}`}>{value}</div>
      <div className="text-[10px] text-bone/60 mt-0.5">{sub}</div>
      {updated && <div className="text-[9px] text-bone/40 mt-1">Updated {new Date(updated).toLocaleDateString()}</div>}
    </div>
  );
}

function ComponentBar({ label, value, max }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div>
      <div className="flex items-center justify-between text-ink-soft mb-1">
        <span>{label}</span>
        <span>{value}/{max}</span>
      </div>
      <div className="h-1 bg-bone rounded-full overflow-hidden">
        <div className="h-full bg-terracotta transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function MetricTile({ icon: Icon, label, value, sub, tone, testId }) {
  const toneCls = {
    forest: "text-forest",
    terracotta: "text-terracotta",
    warn: "text-terracotta",
    muted: "text-ink-soft",
  }[tone] || "text-ink";
  return (
    <div className="border border-line rounded-lg bg-bone/40 p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-ink-soft">
        <Icon className="h-3 w-3" strokeWidth={1.5} /> {label}
      </div>
      <div className={`font-serif text-3xl leading-none mt-1 ${toneCls}`}>{value}</div>
      <div className="text-[10px] text-ink-soft mt-1">{sub}</div>
    </div>
  );
}

function StatusChip({ label, state, good, testId }) {
  const isGood = state === good;
  return (
    <div className={`rounded-full px-3 py-2 flex items-center gap-2 border ${isGood ? "border-forest/40 bg-forest/5" : "border-terracotta/40 bg-terracotta/5"}`} data-testid={testId}>
      {isGood ? <CheckCircle2 className="h-3.5 w-3.5 text-forest" strokeWidth={1.5} /> : <AlertTriangle className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} />}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
        <div className="text-xs text-ink">{state}</div>
      </div>
    </div>
  );
}
